import React, { useEffect, useMemo, useState } from 'react';
import { ArchivedDataEntry } from './ArchivedOverview';
import {
	AccessorKeyColumnDef,
	getCoreRowModel,
	getFilteredRowModel,
	getPaginationRowModel,
	getSortedRowModel,
	PaginationState,
	SortingState,
	useReactTable,
	Table as __Table,
} from '@tanstack/react-table';
import { Establishment } from '../../../../hooks/use-establishments';
import { useTranslation } from 'react-i18next';
import useTableState from '../../useTableState';
import { Table as BTable } from 'react-bootstrap';
import * as ROUTES from '../../../../constants/routes';
import { useNavigate } from 'react-router-dom';
import TableFooter from '../../Components/TableFooter';
import TableBody from '../../Components/TableBody';
import TableHeader from '../../Components/TableHeader';

type Props = {
	data: ArchivedDataEntry[];
	columns: AccessorKeyColumnDef<ArchivedDataEntry, string>[];
	establishmentData: Establishment[];
};

const tableName = 'archived-employees-table';
const Table = ({ data, columns, establishmentData }: Props) => {
	const { t } = useTranslation();
	const navigate = useNavigate();

	const { setPageIndex, setPageSize, pageIndex, pageSize } = useTableState(tableName);
	const [selectedEstablishmentId, setSelectedEstablishmentId] = useState<number | null>(null);

	const [pagination, setPagination] = React.useState<PaginationState>({
		pageIndex,
		pageSize,
	});

	const [sorting, setSorting] = React.useState<SortingState>([
		{
			id: 'lastNameAndInsertion',
			desc: false,
		},
	]);

	const [globalFilter, setGlobalFilter] = React.useState<string>('');

	const filteredData = useMemo(() => {
		if ((selectedEstablishmentId || 0) > 0) {
			return data.filter(
				(nonCode95Entry) => nonCode95Entry.establishment?.id === selectedEstablishmentId,
			);
		}

		return data;
	}, [data, selectedEstablishmentId]);

	useEffect(() => {
		setPageIndex(pagination.pageIndex);
		setPageSize(pagination.pageSize);
	}, [pagination]);

	const table = useReactTable({
		data: filteredData,
		columns,
		getCoreRowModel: getCoreRowModel(),
		getPaginationRowModel: getPaginationRowModel(),
		getSortedRowModel: getSortedRowModel(),
		getFilteredRowModel: getFilteredRowModel(),
		onPaginationChange: setPagination,
		onSortingChange: setSorting,
		enableGlobalFilter: true,
		onGlobalFilterChange: setGlobalFilter,
		state: {
			pagination,
			sorting,
			globalFilter,
		},
	});

	return (
		<>
			<div className="d-flex justify-content-between">
				<div className="d-flex justify-content-start">
					{establishmentData?.length > 0 && (
						<select
							className="form-select"
							onChange={({ target }) => setSelectedEstablishmentId(parseInt(target.value, 10))}
							style={{ minWidth: 250 }}
							value={selectedEstablishmentId || 0}>
							<option value={0}>{t('table.allEstablishments')}</option>
							{establishmentData.map((establishment) => (
								<option key={establishment.id} value={establishment.id}>
									{establishment.name}
								</option>
							))}
						</select>
					)}
				</div>
				<div className="d-flex flex-row align-items-center">
					<label className="me-2">{t('table.search')}: </label>
					<input
						className="form-control form-control-sm d-inline-block mt-1"
						value={globalFilter}
						onChange={(e) => {
							setGlobalFilter(e.target.value);
						}}
					/>
				</div>
			</div>
			<BTable responsive hover size={'l'}>
				<TableHeader table={table as __Table<unknown>} />
				<TableBody
					table={table as __Table<unknown>}
					handleRowClick={(row) => {
						navigate(ROUTES.employeeDetailPage(row.id));
					}}
				/>
			</BTable>
			<TableFooter table={table as __Table<unknown>} />
		</>
	);
};

export default Table;
